/**
 * Catálogo de planos por tenant.
 *
 * Slugs internos padronizados em ambos os tenants: essencial / completo /
 * premium. O LABEL visível por tenant pode divergir (Einstein mantém
 * Diagnóstico / Estudante / Reta Final como nomes de marketing).
 *
 * Price IDs vivem em ENV VARS (não no código), nomes simétricos:
 *   - STRIPE_PRICE_EINSTEIN_ESSENCIAL / _COMPLETO / _PREMIUM
 *   - STRIPE_PRICE_USP_ESSENCIAL / _COMPLETO / _PREMIUM
 *
 * O backend (_shared/stripe.ts) resolve `(tenant, plan)` → priceId via env.
 * O frontend chama POST /api/billing/checkout com { tenant, plan } e o
 * backend valida e cria a Checkout Session.
 */

import type { PlatformTenantSlug } from "@/platform/lib/database.types";

export type PlanSlug = "essencial" | "completo" | "premium";

export interface PlanCatalogEntry {
  slug: PlanSlug;
  tenant: PlatformTenantSlug;
  name: string;
  priceMonthlyBrl: number;
  trialDays: number;
  description: string;
  features: string[];
  featured?: boolean;
  badge?: string;
  /** Slug do env var (sem prefixo VITE_) para validação no backend. */
  envKey: string;
  /** Status comercial do plano. */
  status: "active" | "coming_soon";
}

export const EINSTEIN_PLANS: PlanCatalogEntry[] = [
  {
    slug: "essencial",
    tenant: "einstein",
    name: "Diagnóstico",
    priceMonthlyBrl: 59.9,
    trialDays: 7,
    description:
      "A foto inicial honesta antes do plano completo. Correção e relatório de focos prioritários.",
    features: [
      "Mini-prova objetiva calibrada",
      "1 redação Vunesp curta corrigida",
      "2 discursivas com resposta mínima e excelente",
      "Nota projetada + três focos prioritários",
      "Plano semanal inicial sugerido",
    ],
    envKey: "STRIPE_PRICE_EINSTEIN_ESSENCIAL",
    status: "active",
  },
  {
    slug: "completo",
    tenant: "einstein",
    name: "Estudante",
    priceMonthlyBrl: 159,
    trialDays: 7,
    badge: "Mais escolhido",
    featured: true,
    description:
      "Plano adaptativo completo, correções ilimitadas, simulados e dashboard de evolução.",
    features: [
      "Tudo do Diagnóstico",
      "Plano adaptativo semanal (peso × lacuna × tempo)",
      "Correções ilimitadas de redação e discursivas",
      "Simulado 5h no formato Einstein",
      "Revisão espaçada SM-2 automática",
      "Dashboard de evolução com nota padronizada",
    ],
    envKey: "STRIPE_PRICE_EINSTEIN_COMPLETO",
    status: "active",
  },
  {
    slug: "premium",
    tenant: "einstein",
    name: "Reta Final",
    priceMonthlyBrl: 450,
    trialDays: 7,
    description:
      "Para os 60 dias finais. Inclui tutor virtual, prioridade na correção humana e mentoria semanal.",
    features: [
      "Tudo do Estudante",
      "Tutor virtual com explicações detalhadas",
      "Prioridade fila correção humana revisada",
      "Mentoria pedagógica semanal (30 min)",
      "Estratégia individual de gestão de prova",
      "Análise emocional pré e pós-simulado",
    ],
    envKey: "STRIPE_PRICE_EINSTEIN_PREMIUM",
    status: "active",
  },
];

export const USP_PLANS: PlanCatalogEntry[] = [
  {
    slug: "essencial",
    tenant: "usp",
    name: "Essencial",
    priceMonthlyBrl: 59.9,
    trialDays: 7,
    description:
      "Pipeline completo de 1ª fase + correção de redação Fuvest. Para quem está construindo base.",
    features: [
      "Diagnóstico calibrado em 90 questões",
      "Plano adaptativo Fuvest",
      "Simulado de 1ª fase com nota projetada",
      "Correção de redação Fuvest com rubrica oficial",
      "Dashboard analítico",
    ],
    envKey: "STRIPE_PRICE_USP_ESSENCIAL",
    status: "active",
  },
  {
    slug: "completo",
    tenant: "usp",
    name: "Completo",
    priceMonthlyBrl: 159,
    trialDays: 7,
    badge: "Recomendado",
    featured: true,
    description:
      "Inclui correção de discursivas da 2ª fase por disciplina, calibrada para Eng. Produção.",
    features: [
      "Tudo do Essencial",
      "Correção de discursivas 2ª fase por disciplina",
      "Comparação com resposta-modelo Fuvest",
      "Revisão espaçada SM-2",
      "Tutor virtual",
    ],
    envKey: "STRIPE_PRICE_USP_COMPLETO",
    status: "active",
  },
  {
    slug: "premium",
    tenant: "usp",
    name: "Premium",
    priceMonthlyBrl: 450,
    trialDays: 7,
    description:
      "Inclui mentoria pedagógica semanal e análise emocional para os 90 dias finais.",
    features: [
      "Tudo do Completo",
      "Mentoria pedagógica semanal (45 min)",
      "Prioridade absoluta em fila de correção humana",
      "Análise emocional pré/pós-simulado",
      "Sessão de estratégia individual de prova",
    ],
    envKey: "STRIPE_PRICE_USP_PREMIUM",
    status: "active",
  },
];

export const ALL_PLANS: PlanCatalogEntry[] = [...EINSTEIN_PLANS, ...USP_PLANS];

export function getPlansForTenant(tenant: PlatformTenantSlug): PlanCatalogEntry[] {
  return ALL_PLANS.filter((p) => p.tenant === tenant);
}

export function findPlanBySlug(slug: PlanSlug, tenant: PlatformTenantSlug): PlanCatalogEntry | null {
  return ALL_PLANS.find((p) => p.slug === slug && p.tenant === tenant) ?? null;
}

export function formatBrl(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
}
